/**
 * Conexão ÚNICA do atendimento ao vivo (/api/whatsapp/stream), dividida entre a
 * tela do WhatsApp, o aviso de mensagem nova do layout e o atendimento no
 * celular. Uma aba = uma conexão, por mais componentes que escutem.
 *
 * [Bruno 2026-10-27] "delay no nosso whats do celular para o cockpit". Medido:
 * a mensagem chega ao servidor em 3-6 s; o atraso era a TELA. O EventSource só
 * reconecta sozinho quando a REDE cai. Quando o servidor RESPONDE com erro (502
 * em toda publicação, 401 com token vencido, 429), ele fecha de vez, e a tela
 * ficava sem ao vivo e sem aviso até alguém dar F5. Agora qualquer queda fecha
 * e reabre aqui, com espera crescente (2 s → 30 s), relendo o token; e quem
 * escuta recebe 'conectado' com `reconectou: true` pra buscar o que chegou no
 * buraco (os avisos desse intervalo não voltam).
 */
import { API_BASE } from '../services/api';

const EVENTOS = ['mensagem', 'fila'];
const ESPERA_INICIAL = 2000;
const ESPERA_MAX = 30000;

const ouvintes = new Map(); // evento → Set(fn)
let fonte = null;
let espera = ESPERA_INICIAL;
let timer = null;
let usuarios = 0;
let caiu = false; // já houve queda desde a última conexão boa
let lerToken = () => { try { return localStorage.getItem('token'); } catch (_) { return null; } };
let base = API_BASE;

function disparar(evento, dados) {
  const s = ouvintes.get(evento);
  if (!s) return;
  for (const fn of [...s]) { try { fn(dados); } catch (_) { /* ouvinte com erro não derruba os outros */ } }
}

function agendar() {
  if (timer || usuarios <= 0) return;
  timer = setTimeout(() => { timer = null; abrir(); }, espera);
  espera = Math.min(espera * 2, ESPERA_MAX);
}

function abrir() {
  if (fonte || usuarios <= 0 || typeof window === 'undefined' || typeof window.EventSource === 'undefined') return;
  const token = lerToken();
  if (!token) { agendar(); return; }
  const es = new window.EventSource(`${base}/whatsapp/stream?token=${encodeURIComponent(token)}`);
  fonte = es;
  es.onopen = () => {
    if (fonte !== es) return;
    espera = ESPERA_INICIAL;
    const reconectou = caiu;
    caiu = false;
    disparar('conectado', { reconectou });
  };
  for (const ev of EVENTOS) {
    es.addEventListener(ev, (e) => {
      if (fonte !== es) return;
      let d = {};
      try { d = JSON.parse(e.data) || {}; } catch (_) { /* aviso sem corpo: quem escuta recarrega tudo */ }
      disparar(ev, d);
    });
  }
  es.onerror = () => {
    if (fonte !== es) return;
    try { es.close(); } catch (_) { /* ok */ }
    fonte = null;
    caiu = true;
    disparar('desconectado', {});
    agendar();
  };
}

function fechar() {
  if (timer) { clearTimeout(timer); timer = null; }
  if (fonte) { try { fonte.close(); } catch (_) { /* ok */ } fonte = null; }
  espera = ESPERA_INICIAL;
  caiu = false;
}

/** Voltou a rede ou a aba: não espera o fim da espera crescente. */
function tentarJa() {
  if (fonte || usuarios <= 0) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  if (timer) { clearTimeout(timer); timer = null; }
  espera = ESPERA_INICIAL;
  abrir();
}
let ganchos = false;
function instalarGanchos() {
  if (ganchos || typeof window === 'undefined') return;
  ganchos = true;
  window.addEventListener('online', tentarJa);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', tentarJa);
}

/** Escuta um evento ('mensagem', 'fila', 'conectado', 'desconectado'); devolve a função que para. */
export function ouvir(evento, fn) {
  if (!ouvintes.has(evento)) ouvintes.set(evento, new Set());
  ouvintes.get(evento).add(fn);
  return () => { ouvintes.get(evento)?.delete(fn); };
}

/**
 * Quem precisa da conexão chama `usar()` e guarda o retorno pra soltar.
 * `token` troca a leitura do token (o atendimento no celular guarda o seu).
 */
export function usar({ token } = {}) {
  if (typeof token === 'function') lerToken = token;
  usuarios += 1;
  instalarGanchos();
  abrir();
  return () => {
    usuarios = Math.max(0, usuarios - 1);
    if (!usuarios) fechar();
  };
}

export function conectado() { return !!fonte && fonte.readyState === 1; }

/** Só pro harness. */
export function _reiniciar({ base: b } = {}) {
  fechar();
  usuarios = 0;
  ouvintes.clear();
  if (b !== undefined) base = b;
}
